import { Badge, Box, Flex, HStack, Progress, Text } from '@chakra-ui/react';

import { Saving } from '@aslanpay/portal-common-lib';

import { SavingStatusValues, SavingTypeLabels } from 'app/constants/savings';

import SavingPotImage from './SavingPotImage';
import { formatSavingAmount } from './savingFormatters';

interface SavingPotCardProps {
  saving: Saving;
  onOpen: (saving: Saving) => void;
}

function SavingPotCard({ saving, onOpen }: SavingPotCardProps) {
  const status = SavingStatusValues[saving.status];
  const name = saving.name || 'Unnamed pot';
  const progress =
    saving.targetAmount && saving.balance != null
      ? Math.min(100, (saving.balance / saving.targetAmount) * 100)
      : undefined;

  return (
    <Box
      as="button"
      type="button"
      textAlign="left"
      w="full"
      border="1px solid"
      borderColor="brand.lightGray1"
      borderRadius="10px"
      overflow="hidden"
      _hover={{ borderColor: 'brand.purple' }}
      onClick={() => onOpen(saving)}
      aria-label={`Open ${name}`}
    >
      <SavingPotImage imageId={saving.imageId} name={name} variant="THUMBNAIL" h="120px" />
      <Flex flexDir="column" gap="8px" p="12px">
        <Text textStyle="smallBold" noOfLines={1}>
          {name}
        </Text>
        <HStack spacing="6px" flexWrap="wrap">
          <Badge colorScheme="badge.registered">{SavingTypeLabels[saving.savingType]}</Badge>
          <Badge colorScheme={`badge.${status?.color}`}>{status?.value}</Badge>
        </HStack>
        <Flex justify="space-between" align="baseline">
          <Text textStyle="smallBold">{formatSavingAmount(saving.balance, saving.currency)}</Text>
          {saving.targetAmount != null && (
            <Text textStyle="extraSmallRegular" color="brand.darkGray">
              {`of ${formatSavingAmount(saving.targetAmount, saving.currency)}`}
            </Text>
          )}
        </Flex>
        {progress != null && (
          <Progress value={progress} size="xs" colorScheme="purple" borderRadius="full" />
        )}
        {saving.recurringAmount != null && (
          <Text textStyle="extraSmallRegular" color="brand.darkGray">
            {`${formatSavingAmount(saving.recurringAmount, saving.currency)} each payday`}
          </Text>
        )}
      </Flex>
    </Box>
  );
}

export default SavingPotCard;
